import React, { useState, useMemo } from "react"
import { formatFeatureLabel } from "../../utils/organizationApiHelpers"

const BarContainer = ({ generatedImagesPercent = {}, generatedImages = {} }) => {
  const lang =
    typeof window !== "undefined"
      ? localStorage.getItem("language") || "ar"
      : "ar"

  // 🧠 Map API data into chart format
  const featureEntries = useMemo(() => {
    const keys = Object.keys(generatedImagesPercent || {})
    return keys
      .map((key) => {
        const tooltipName = formatFeatureLabel(key, lang === "ar" ? "ar" : "en")
        const rawPercent = generatedImagesPercent[key]
        return [
          tooltipName,
          {
            count: generatedImages[key] ? parseInt(generatedImages[key]) : 0,
            percentage: rawPercent
              ? parseFloat(String(rawPercent).replace("%", "")) || 0
              : 0,
          },
        ]
      })
      // Features with no generated images add invisible zero-width segments and
      // only shift the color cycle, so drop them.
      .filter(([, obj]) => obj.percentage > 0)
  }, [generatedImagesPercent, generatedImages, lang])

  const colors = ["#5b8def", "#4169e1", "#2b3e9a", "#0e1738", "#0a0f2a"]
  const totalPercentage = featureEntries.reduce(
    (sum, [, obj]) => sum + (obj.percentage || 0),
    0
  )
  const normalizationFactor = totalPercentage > 0 ? 100 / totalPercentage : 0

  const [tooltip, setTooltip] = useState({
    visible: false,
    name: "",
    count: 0,
    percentage: "",
    x: 0,
    y: 0,
  })

  const showTooltip = (e, feature, count, percentage) => {
    const rect = e.target.getBoundingClientRect()
    setTooltip({
      visible: true,
      name: feature,
      count,
      percentage,
      x: rect.left + rect.width / 2,
      y: rect.top,
    })
  }

  const hideTooltip = () => setTooltip((prev) => ({ ...prev, visible: false }))

  return (
    <div style={{ position: "relative" }}>
      {/* Progress Bar */}
      <div
        style={{
          position: "relative",
          width: "100%",
          height: "40px",
          borderRadius: "10px",
          overflow: "hidden",
          display: "flex",
          background: "#f0f2f5",
        }}
      >
        {
          featureEntries.reduce(
            (acc, [feature, obj], i) => {
              const prevWidth = acc.total
              const adjustedWidth = (obj.percentage || 0) * normalizationFactor
              acc.total += adjustedWidth

              acc.bars.push(
                <div
                  key={i}
                  style={{
                    position: "absolute",
                    left: `${prevWidth}%`,
                    width: `${adjustedWidth}%`,
                    height: "100%",
                    backgroundColor: colors[i % colors.length],
                    transition: "width 0.3s ease",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) =>
                    showTooltip(e, feature, obj.count, obj.percentage)
                  }
                  onMouseLeave={hideTooltip}
                />
              )
              return acc
            },
            { total: 0, bars: [] }
          ).bars
        }
      </div>

      {/* Tooltip */}
      {tooltip.visible && (
        <div
          style={{
            position: "fixed",
            left: tooltip.x,
            top: tooltip.y - 8,
            transform: "translate(-50%, -100%)",
            background: "white",
            padding: "10px 14px",
            borderRadius: "8px",
            boxShadow: "0px 4px 12px rgba(0,0,0,0.15)",
            pointerEvents: "none",
            zIndex: 1000,
            textAlign: "center",
            minWidth: "130px",
          }}
        >
          <div
            style={{
              fontSize: "14px",
              color: "#4B5563",
              marginBottom: "4px",
              fontWeight: 500,
              direction: lang === "ar" ? "rtl" : "ltr",
            }}
          >
            {tooltip.name}
          </div>
          <div
            style={{
              fontSize: "15px",
              fontWeight: 600,
              color: "#111827",
              direction: lang === "ar" ? "rtl" : "ltr",
            }}
          >
            {tooltip.count.toLocaleString()} | {tooltip.percentage}%
          </div>

          {/* Arrow */}
          <div
            style={{
              position: "absolute",
              bottom: "-8px",
              left: "50%",
              transform: "translateX(-50%)",
              width: 0,
              height: 0,
              borderLeft: "8px solid transparent",
              borderRight: "8px solid transparent",
              borderTop: "8px solid white",
              filter: "drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.1))",
            }}
          />
        </div>
      )}
    </div>
  )
}

export default BarContainer
